
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, Plus, FileText, ChevronRight, X, Calendar, User, Tag, DollarSign, Trash2, Save, Download, RotateCcw, Check, Edit2, MapPin, Globe, Building2, ChevronDown, FileDown, Box, UserCircle, Hash, CheckCircle2, Truck, Info, Clock } from 'lucide-react';
import { Estimate, EstimateStatus, Client, EstimateItem, Product } from './types';
import { Card, Badge } from './SharedComponents';

// Modal para definição de local específico (Logística)
const LocationModal = ({ onClose, onApply, currentAddress }: any) => {
  const [address, setAddress] = useState(currentAddress || {
    street: '',
    city: '',
    state: '',
    zip: ''
  });

  return (
    <div className="fixed inset-0 bg-secondary/90 backdrop-blur-sm z-[150] flex items-center justify-center p-6 animate-in fade-in duration-300">
      <div className="bg-white rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-8 bg-secondary text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-black uppercase tracking-tight">Localização da OS</h2>
            <p className="text-[9px] font-bold text-primary uppercase tracking-widest">Definir endereço de montagem</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl"><X size={24} /></button>
        </div>
        
        <div className="p-8 space-y-6">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Logradouro / Rua e Número</label>
            <textarea 
              autoFocus
              className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary text-sm focus:border-primary transition-all resize-none h-24 shadow-inner"
              placeholder="Ex: Av. das Flores, 1000 - Bloco B"
              value={address.street}
              onChange={e => setAddress({...address, street: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Cidade</label>
              <input 
                className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary text-sm focus:border-primary transition-all shadow-inner"
                placeholder="Sorocaba"
                value={address.city}
                onChange={e => setAddress({...address, city: e.target.value})}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">UF / Estado</label>
              <input 
                className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary text-sm focus:border-primary transition-all shadow-inner"
                placeholder="SP"
                maxLength={2}
                value={address.state}
                onChange={e => setAddress({...address, state: e.target.value.toUpperCase()})}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">CEP</label>
            <input 
              className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary text-sm focus:border-primary transition-all shadow-inner"
              placeholder="00000-000"
              value={address.zip}
              onChange={e => setAddress({...address, zip: e.target.value})}
            />
          </div>

          <div className="pt-4 flex gap-4">
            <button onClick={onClose} className="flex-1 py-4 border-2 border-gray-100 rounded-2xl font-black text-gray-400 uppercase text-[10px] tracking-widest hover:bg-gray-50">Cancelar</button>
            <button 
              onClick={() => onApply(address)} 
              className="flex-[2] py-4 bg-primary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 transition-all"
            >
              Confirmar Endereço
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EstimatesPage = ({ estimates, clients, products, onAddEstimate, onConvertToOrder }: any) => {
  const [view, setView] = useState<'LIST' | 'CREATE' | 'DETAILS'>('LIST');
  const [selectedEstimate, setSelectedEstimate] = useState<Estimate | null>(null);
  const [search, setSearch] = useState('');

  const nextNumber = useMemo(() => {
    const count = estimates.length + 1;
    return `EST-${count.toString().padStart(6, '0')}`;
  }, [estimates]);

  const stats = useMemo(() => {
    const counts: Record<string, number> = {
      [EstimateStatus.RASCUNHO]: 0,
      [EstimateStatus.ENVIADO]: 0,
      [EstimateStatus.EXPIRADO]: 0,
      [EstimateStatus.DECLINADO]: 0,
      [EstimateStatus.ACEITO]: 0,
    };
    estimates.forEach((e: Estimate) => {
      counts[e.status]++;
    });
    return counts;
  }, [estimates]);

  if (view === 'CREATE') {
    return (
      <NewEstimateForm 
        clients={clients} 
        products={products}
        nextNumber={nextNumber}
        onClose={() => setView('LIST')} 
        onSubmit={(e: Estimate) => { onAddEstimate(e); setView('LIST'); }} 
      />
    );
  }

  if (view === 'DETAILS' && selectedEstimate) {
    return (
      <EstimateDetails 
        estimate={selectedEstimate} 
        client={clients.find((c: any) => c.id === selectedEstimate.clientId)}
        onClose={() => { setView('LIST'); setSelectedEstimate(null); }}
        onConvertToOrder={(est: Estimate) => {
          if (onConvertToOrder) onConvertToOrder(est);
          setView('LIST');
          setSelectedEstimate(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 w-full px-8 md:px-16 lg:px-24 py-10">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-black text-secondary uppercase tracking-tighter">Orçamentos</h1>
          <p className="text-gray-400 text-sm font-medium italic">Gestão Comercial e Propostas</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {(Object.values(EstimateStatus) as string[]).map(status => (
          <Card key={status} className="p-4 bg-white border border-slate-100 shadow-sm flex flex-col gap-1">
            <p className={`text-[9px] font-black uppercase tracking-widest ${getStatusColor(status as EstimateStatus)}`}>
              {status}
            </p>
            <p className="text-xl font-black text-secondary">{stats[status]} <span className="text-[10px] text-gray-300 font-bold">/ {estimates.length}</span></p>
          </Card>
        ))}
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <button onClick={() => setView('CREATE')} className="w-full md:w-auto bg-primary text-white px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 hover:brightness-110 transition-all shadow-lg shadow-primary/20 active:scale-95 shrink-0">
          <Plus size={16} /> Novo Orçamento
        </button>
        <div className="relative w-full md:w-96 group">
           <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-primary transition-colors" size={18} />
           <input 
            placeholder="Procurar número ou cliente..." 
            className="w-full pl-12 pr-6 py-3.5 bg-white border border-slate-200 rounded-[18px] outline-none text-sm font-bold text-secondary focus:border-primary transition-all shadow-sm" 
            value={search}
            onChange={e => setSearch(e.target.value)}
           />
        </div>
      </div>

      <Card className="overflow-hidden border-none shadow-xl bg-white rounded-[32px]">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/80 text-slate-400 text-[10px] uppercase font-black tracking-[0.15em] border-b border-slate-100">
                <th className="px-8 py-5">Orçamento #</th>
                <th className="px-8 py-5">Data / Exp.</th>
                <th className="px-8 py-5">Cliente Parceiro</th>
                <th className="px-8 py-5 text-right">Valor Total</th>
                <th className="px-8 py-5 text-center">Status</th>
                <th className="px-8 py-5 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {estimates.length > 0 ? estimates.filter((e:any) => e.number.includes(search)).map((e: Estimate) => (
                <tr key={e.id} className="hover:bg-slate-50/50 transition-colors group cursor-default">
                  <td className="px-8 py-5 font-black text-secondary text-sm">{e.number}</td>
                  <td className="px-8 py-5">
                    <p className="text-xs font-bold text-secondary">{e.date}</p>
                    <p className="text-[10px] text-gray-300 font-medium italic">Vence em {e.expiryDate}</p>
                  </td>
                  <td className="px-8 py-5">
                    <span className="text-xs font-bold text-slate-600 uppercase">{clients.find((c: any) => c.id === e.clientId)?.empresa || 'Cliente não definido'}</span>
                  </td>
                  <td className="px-8 py-5 text-right font-black text-secondary">R$ {e.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                  <td className="px-8 py-5 text-center"><Badge className={getStatusColor(e.status)}>{e.status}</Badge></td>
                  <td className="px-8 py-5 text-right">
                    <button 
                      onClick={() => { setSelectedEstimate(e); setView('DETAILS'); }}
                      className="p-2.5 text-gray-300 hover:text-primary transition-all"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="px-8 py-24 text-center text-slate-200 font-black uppercase tracking-[0.3em] text-xs italic">Nenhum orçamento emitido</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const EstimateDetails = ({ estimate, client, onClose, onConvertToOrder }: any) => {
  return (
    <div className="space-y-8 animate-in fade-in duration-500 w-full px-8 md:px-16 lg:px-24 py-10">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl text-gray-400 transition-all"><ChevronRight size={24} className="rotate-180" /></button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-black text-secondary uppercase tracking-tighter">{estimate.number}</h1>
              <Badge className={getStatusColor(estimate.status)}>{estimate.status}</Badge>
            </div>
            <p className="text-gray-400 text-sm font-medium italic">Detalhes da Proposta Comercial</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button className="bg-white border-2 border-slate-100 text-slate-400 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-slate-50 transition-all shadow-sm">
            <Download size={16} /> Exportar PDF
          </button>
          {estimate.status !== EstimateStatus.ACEITO && (
            <button 
              onClick={() => onConvertToOrder(estimate)}
              className="bg-primary text-white px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:brightness-110 transition-all shadow-lg shadow-primary/20 active:scale-95"
            >
              <CheckCircle2 size={16} /> Aprovar e Gerar OS
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <Card className="p-8 bg-white border border-slate-100 shadow-sm rounded-[32px]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Data de Emissão</p>
                <p className="text-sm font-bold text-secondary">{estimate.date}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Validade</p>
                <p className="text-sm font-bold text-secondary">{estimate.expiryDate}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Montagem</p>
                <p className="text-sm font-bold text-secondary">{estimate.assemblyDate || 'Não definida'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Retirada</p>
                <p className="text-sm font-bold text-secondary">{estimate.removalDate || 'Não definida'}</p>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-xs font-black text-secondary uppercase tracking-widest flex items-center gap-2">
                <Box size={16} className="text-primary" /> Itens da Proposta
              </h3>
              <div className="border border-slate-100 rounded-2xl overflow-hidden">
                <table className="w-full text-left text-[10px]">
                  <thead className="bg-slate-50 font-black uppercase text-slate-400 tracking-widest border-b border-slate-100">
                    <tr>
                      <th className="p-4">Descrição</th>
                      <th className="p-4 text-center">Qtd</th>
                      <th className="p-4 text-center">Unitário</th>
                      <th className="p-4 text-right pr-6">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {estimate.items.map((item: any) => (
                      <tr key={item.id}>
                        <td className="p-4">
                          <p className="font-black text-secondary uppercase">{item.description}</p>
                          <p className="text-[9px] text-slate-400 font-medium">{item.longDescription}</p>
                        </td>
                        <td className="p-4 text-center font-bold text-secondary">{item.qty}</td>
                        <td className="p-4 text-center font-bold text-secondary">R$ {item.rate.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                        <td className="p-4 text-right pr-6 font-black text-secondary">R$ {item.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {(estimate.clientNote || estimate.termsConditions) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10 pt-10 border-t border-slate-50">
                {estimate.clientNote && (
                  <div className="space-y-2">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Observações</p>
                    <p className="text-xs text-slate-600 leading-relaxed">{estimate.clientNote}</p>
                  </div>
                )}
                {estimate.termsConditions && (
                  <div className="space-y-2">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Termos e Condições</p>
                    <p className="text-xs text-slate-600 leading-relaxed">{estimate.termsConditions}</p>
                  </div>
                )}
              </div>
            )}
          </Card>

          {estimate.hasThirdPartyMaterials && (
            <div className="bg-amber-50 border-2 border-amber-100 p-8 rounded-[32px] flex gap-6">
              <div className="bg-amber-500 text-white p-4 rounded-2xl h-fit shadow-lg shadow-amber-500/20">
                <Truck size={24} />
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-black text-amber-900 uppercase tracking-tight">Materiais de Terceiros Identificados</h4>
                <p className="text-xs text-amber-800/70 font-medium leading-relaxed">{estimate.thirdPartyMaterialsNote}</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-8 bg-white border border-slate-100 shadow-sm rounded-[32px] space-y-6">
            <div className="space-y-4">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Cliente / Parceiro</p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400">
                  <Building2 size={24} />
                </div>
                <div>
                  <p className="text-sm font-black text-secondary uppercase">{client?.empresa || 'Não definido'}</p>
                  <p className="text-[10px] text-slate-400 font-bold">{client?.contatoResponsavel || 'Sem contato'}</p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-50 space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-slate-400 uppercase tracking-widest">Subtotal</span>
                <span className="font-black text-secondary">R$ {estimate.subTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              {estimate.adjustment !== 0 && (
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-400 uppercase tracking-widest">Ajuste / Frete</span>
                  <span className="font-black text-secondary">R$ {estimate.adjustment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="pt-4 border-t border-slate-100 flex justify-between items-end">
                <div>
                  <p className="text-[9px] font-black text-primary uppercase tracking-widest mb-1">Total Geral</p>
                  <p className="text-2xl font-black text-secondary tracking-tighter">R$ {estimate.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-secondary text-white rounded-[32px] space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg"><UserCircle size={18} /></div>
              <div>
                <p className="text-[8px] font-black text-white/40 uppercase tracking-widest">Agente Responsável</p>
                <p className="text-xs font-bold">{estimate.saleAgent || 'Não atribuído'}</p>
              </div>
            </div>
            {estimate.reference && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-lg"><Hash size={18} /></div>
                <div>
                  <p className="text-[8px] font-black text-white/40 uppercase tracking-widest">Referência</p>
                  <p className="text-xs font-bold">{estimate.reference}</p>
                </div>
              </div>
            )}
            {estimate.tags && estimate.tags.length > 0 && (
              <div className="pt-4 border-t border-white/10 flex flex-wrap gap-2">
                {estimate.tags.map((tag: string) => (
                  <Badge key={tag} className="bg-white/5 text-white/60 border-white/10 text-[8px]">{tag}</Badge>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

const getStatusColor = (status: string) => {
  switch(status) {
    case EstimateStatus.RASCUNHO: return 'text-gray-500 bg-slate-50 border-slate-100';
    case EstimateStatus.ENVIADO: return 'text-blue-500 bg-blue-50 border-blue-100';
    case EstimateStatus.EXPIRADO: return 'text-amber-500 bg-amber-100 border-amber-100';
    case EstimateStatus.DECLINADO: return 'text-red-500 bg-red-50 border-red-100';
    case EstimateStatus.ACEITO: return 'text-green-500 bg-green-50 border-green-100';
    default: return 'text-gray-400';
  }
};

const AutocompleteInput = ({ value, onChange, products, onSelect }: any) => {
  const [search, setSearch] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    if (!search || search.length < 1) return [];
    return products.filter((p: Product) => 
      p.name.toLowerCase().includes(search.toLowerCase()) || 
      p.sku.toLowerCase().includes(search.toLowerCase())
    ).slice(0, 5);
  }, [search, products]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={containerRef}>
      <input 
        className="w-full p-3.5 bg-white border border-slate-200 rounded-xl outline-none font-bold text-secondary focus:border-primary transition-all text-[11px] placeholder:text-slate-300 shadow-sm"
        placeholder="Digite o material..."
        value={search}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          setSearch(e.target.value);
          onChange(e.target.value);
          setIsOpen(true);
        }}
      />
      {isOpen && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-100 rounded-2xl shadow-2xl z-[60] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {suggestions.map((p: Product) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                onSelect(p);
                setSearch(p.name);
                setIsOpen(false);
              }}
              className="w-full text-left p-4 hover:bg-primary/5 transition-colors border-b border-slate-50 last:border-none flex justify-between items-center group"
            >
              <div>
                <p className="text-[10px] font-black text-secondary uppercase group-hover:text-primary">{p.name}</p>
                <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest">REF: {p.sku}</p>
              </div>
              <Badge className="bg-gray-50 text-gray-400 border-none text-[8px]">{p.quantity} UN</Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const NewEstimateForm = ({ clients, products, onClose, onSubmit, nextNumber }: any) => {
  const [formData, setFormData] = useState<any>(() => ({
    clientId: '',
    number: nextNumber,
    date: new Date().toISOString().split('T')[0],
    expiryDate: new Date(Date.now() + 604800000).toISOString().split('T')[0],
    assemblyDate: '',
    removalDate: '',
    currency: 'BRL R$',
    status: EstimateStatus.RASCUNHO,
    reference: '',
    saleAgent: 'Magnun Gestor',
    discountType: 'Percentage',
    adminNote: '',
    clientNote: '',
    termsConditions: '',
    tags: [],
    items: [{ id: '1', name: '', description: '', longDescription: '', qty: 1, rate: 0, tax: 0, amount: 0 }],
    subTotal: 0,
    discount: 0,
    adjustment: 0,
    total: 0,
    hasThirdPartyMaterials: false,
    thirdPartyMaterialsNote: ''
  }));

  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [customLocation, setCustomLocation] = useState<any>(null);

  const finalTotal = useMemo(() => {
    const sub = Number(formData.subTotal) || 0;
    const disc = Number(formData.discount) || 0;
    const adj = Number(formData.adjustment) || 0;
    
    let discountValue = 0;
    if (formData.discountType === 'Percentage') {
        discountValue = sub * (disc / 100);
    } else {
        discountValue = disc;
    }

    return sub - discountValue + adj;
  }, [formData.subTotal, formData.discount, formData.discountType, formData.adjustment]);

  const addItem = () => {
    const newItem: EstimateItem = {
      id: Date.now().toString(),
      name: '',
      description: '',
      longDescription: '',
      qty: 1,
      rate: 0,
      tax: 0,
      amount: 0
    };
    setFormData((prev: any) => ({ ...prev, items: [...(prev.items || []), newItem] }));
  };

  const removeItem = (id: string) => {
    setFormData((prev: any) => {
      const updatedItems = prev.items?.filter((i: any) => i.id !== id);
      const subTotal = updatedItems?.reduce((acc: number, i: any) => acc + (Number(i.amount) || 0), 0) || 0;
      return { ...prev, items: updatedItems, subTotal };
    });
  };

  const handleAddTag = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim().toUpperCase())) {
      setFormData((prev: any) => ({ ...prev, tags: [...(prev.tags || []), tagInput.trim().toUpperCase()] }));
      setTagInput('');
      setShowTagInput(false);
    }
  };

  const updateItem = (id: string, field: keyof EstimateItem, value: any) => {
    setFormData((prev: any) => {
      const updatedItems = prev.items?.map((i: any) => {
        if (i.id === id) {
          const updated = { ...i, [field]: value };
          updated.amount = (Number(updated.qty) || 0) * (Number(updated.rate) || 0);
          return updated;
        }
        return i;
      });

      const subTotal = updatedItems?.reduce((acc: number, i: any) => acc + (Number(i.amount) || 0), 0) || 0;
      return { ...prev, items: updatedItems, subTotal };
    });
  };

  const selectedClient = clients.find((c: any) => c.id === formData.clientId);

  return (
    <div className="bg-slate-50 min-h-screen pb-20 animate-in fade-in duration-500 w-full overflow-x-hidden">
      <div className="bg-white border-b border-slate-100 px-6 py-4 flex justify-between items-center sticky top-0 z-[50] shadow-sm w-full">
        <div className="flex items-center gap-4">
           <div className="bg-primary/10 p-2.5 rounded-xl text-primary shadow-sm"><FileText size={22} /></div>
           <div>
              <h2 className="text-lg font-black text-secondary uppercase tracking-tight leading-none">Novo Orçamento</h2>
              <p className="text-[8px] font-black text-primary uppercase tracking-[0.2em] mt-1 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></div> Live Sync Inventário
              </p>
           </div>
        </div>
        <div className="flex items-center gap-3">
           <div className="text-right mr-2 hidden sm:block">
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Protocolo OS</p>
              <p className="text-xs font-black text-secondary">{formData.number}</p>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-xl text-gray-300 transition-all hover:text-primary"><X size={24} /></button>
        </div>
      </div>

      <div className="w-full px-6 mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <Card className="p-8 bg-white border border-slate-100 shadow-sm rounded-[32px] space-y-10">
             <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                      <User size={12} className="text-primary" /> Cliente Solicitante
                    </label>
                    <div className="relative">
                      <select 
                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none focus:border-primary font-bold text-secondary text-sm appearance-none cursor-pointer transition-all"
                        value={formData.clientId}
                        onChange={e => setFormData({...formData, clientId: e.target.value})}
                      >
                        <option value="">Selecione um parceiro cadastrado...</option>
                        {clients.map((c: Client) => <option key={c.id} value={c.id}>{c.empresa}</option>)}
                      </select>
                      <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                           <Calendar size={12} className="text-primary" /> Emissão
                        </label>
                        <input type="date" className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary text-xs focus:border-primary" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                     </div>
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                           <RotateCcw size={12} className="text-amber-500" /> Validade
                        </label>
                        <input type="date" className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary text-xs focus:border-primary" value={formData.expiryDate} onChange={e => setFormData({...formData, expiryDate: e.target.value})} />
                     </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                           <Calendar size={12} className="text-primary" /> Data da Montagem
                        </label>
                        <input type="date" className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary text-xs focus:border-primary" value={formData.assemblyDate} onChange={e => setFormData({...formData, assemblyDate: e.target.value})} />
                     </div>
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                           <Clock size={12} className="text-primary" /> Data de Retirada
                        </label>
                        <input type="date" className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary text-xs focus:border-primary" value={formData.removalDate} onChange={e => setFormData({...formData, removalDate: e.target.value})} />
                     </div>
                  </div>
                </div>

                <div className="bg-slate-50/50 p-6 rounded-[32px] border border-slate-100 space-y-4">
                   <p className="text-[9px] font-black text-primary uppercase tracking-[0.2em] flex items-center gap-2">
                      <MapPin size={12} /> Logística do Evento
                   </p>
                   {customLocation ? (
                     <div className="space-y-1 animate-in slide-in-from-top-1">
                        <p className="text-[10px] font-black text-secondary uppercase leading-tight">{customLocation.street}</p>
                        <p className="text-[9px] text-slate-400 font-bold uppercase">{customLocation.city} - {customLocation.state}</p>
                     </div>
                   ) : (
                     <p className="text-[10px] text-slate-400 font-medium leading-relaxed italic">
                        {selectedClient ? `Herdando endereço de ${selectedClient.empresa}` : 'Aguardando seleção do cliente ou local específico.'}
                     </p>
                   )}
                   <div className="pt-2">
                      <button 
                        type="button" 
                        onClick={() => setIsLocationModalOpen(true)}
                        className="text-[9px] font-black text-secondary uppercase tracking-widest hover:underline flex items-center gap-1.5 border border-secondary/20 rounded-lg px-3 py-1.5 transition-all active:scale-95 bg-white shadow-sm"
                      >
                         <Edit2 size={10} /> Definir Local Específico
                      </button>
                   </div>
                </div>
             </div>

             <div className="pt-8 border-t border-slate-100 space-y-6">
                <div className="flex justify-between items-center">
                   <h3 className="text-xs font-black text-secondary uppercase tracking-widest flex items-center gap-2">
                     <Box size={16} className="text-primary" /> Itens e Serviços de Locação
                   </h3>
                   <Badge className="bg-primary/5 text-primary border-none text-[8px] px-3 py-1">PROPOSTA LIVE</Badge>
                </div>

                <div className="bg-white border border-slate-200 rounded-[24px] overflow-hidden shadow-sm">
                   <table className="w-full text-left text-[10px] border-collapse">
                      <thead className="bg-slate-50 font-black uppercase text-slate-400 tracking-widest border-b border-slate-200">
                         <tr>
                            <th className="p-4 w-[35%]">Material / Descrição</th>
                            <th className="p-4 w-[25%]">Notas Técnicas</th>
                            <th className="p-4 w-20 text-center">Qtd</th>
                            <th className="p-4 w-28 text-center">Preço Unit.</th>
                            <th className="p-4 text-right pr-6">Subtotal</th>
                            <th className="p-4 w-10"></th>
                         </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                         {formData.items?.map((item: EstimateItem) => (
                           <tr key={item.id} className="hover:bg-slate-50/30 transition-all duration-200">
                              <td className="p-3">
                                <AutocompleteInput 
                                  value={item.description}
                                  products={products}
                                  onSelect={(p: Product) => {
                                    updateItem(item.id, 'description', p.name);
                                    updateItem(item.id, 'rate', 0);
                                    updateItem(item.id, 'longDescription', `REF: ${p.sku} | COR: ${p.color}`);
                                  }}
                                  onChange={(val: string) => updateItem(item.id, 'description', val)}
                                />
                              </td>
                              <td className="p-3">
                                <textarea 
                                  className="w-full p-3 bg-white border border-gray-100 rounded-xl outline-none h-12 resize-none font-bold text-slate-500 focus:border-primary text-[10px] shadow-sm placeholder:text-slate-300" 
                                  placeholder="Detalhes adicionais..."
                                  value={item.longDescription}
                                  onChange={e => updateItem(item.id, 'longDescription', e.target.value)}
                                />
                              </td>
                              <td className="p-3 text-center">
                                <input 
                                  type="number" 
                                  className="w-full p-3 bg-white border border-gray-100 rounded-xl outline-none font-black text-center focus:border-primary text-xs shadow-sm text-secondary" 
                                  value={item.qty}
                                  onChange={e => updateItem(item.id, 'qty', e.target.value)}
                                />
                              </td>
                              <td className="p-3 text-center">
                                <input 
                                  type="number" 
                                  className="w-full p-3 bg-white border border-gray-100 rounded-xl outline-none font-black text-center focus:border-primary text-xs shadow-sm text-secondary" 
                                  value={item.rate}
                                  onChange={e => updateItem(item.id, 'rate', e.target.value)}
                                />
                              </td>
                              <td className="p-3 text-right pr-6">
                                 <p className="font-black text-secondary text-[11px]">R$ {(Number(item.amount) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                              </td>
                              <td className="p-3">
                                 <button onClick={() => removeItem(item.id)} className="text-slate-300 hover:text-red-500 transition-all"><Trash2 size={16} /></button>
                              </td>
                           </tr>
                         ))}
                      </tbody>
                   </table>
                   <div className="p-4 bg-white border-t border-slate-100 flex justify-between items-center">
                      <button onClick={addItem} className="flex items-center gap-2 text-[9px] font-black text-primary uppercase tracking-widest hover:underline transition-all">
                         <Plus size={16} strokeWidth={4} /> Adicionar Nova Linha de Material
                      </button>
                      <Badge className="bg-slate-50 text-slate-400 border-none text-[8px] uppercase">{formData.items?.length} ITENS LISTADOS</Badge>
                   </div>
                </div>

                <div className="pt-8 border-t border-slate-100">
                   <div className={`p-8 rounded-[32px] border-2 transition-all ${formData.hasThirdPartyMaterials ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'}`}>
                      <div className="flex items-center justify-between mb-6">
                         <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-2xl shadow-sm transition-all ${formData.hasThirdPartyMaterials ? 'bg-amber-500 text-white' : 'bg-white text-slate-300'}`}>
                               <Truck size={24} />
                            </div>
                            <div>
                               <h4 className={`text-sm font-black uppercase tracking-tight ${formData.hasThirdPartyMaterials ? 'text-amber-900' : 'text-slate-500'}`}>Adendo: Materiais de Terceiros</h4>
                               <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Informar se haverá itens não pertencentes à frota própria</p>
                            </div>
                         </div>
                         <button 
                            type="button"
                            onClick={() => setFormData({...formData, hasThirdPartyMaterials: !formData.hasThirdPartyMaterials})}
                            className={`w-14 h-8 rounded-full p-1 transition-all flex items-center ${formData.hasThirdPartyMaterials ? 'bg-amber-500 justify-end' : 'bg-slate-200 justify-start'}`}
                         >
                            <div className="w-6 h-6 bg-white rounded-full shadow-md"></div>
                         </button>
                      </div>

                      {formData.hasThirdPartyMaterials && (
                        <div className="animate-in slide-in-from-top-2 duration-300">
                           <textarea 
                             rows={3}
                             className="w-full bg-white border border-amber-100 p-5 rounded-2xl outline-none font-bold text-amber-900 text-xs focus:ring-4 focus:ring-amber-200/20 transition-all resize-none shadow-sm placeholder:text-amber-200"
                             placeholder="Descreva aqui os materiais de terceiros (ex: Gerador alugado da empresa X, Iluminação de parceiro Y...)"
                             value={formData.thirdPartyMaterialsNote}
                             onChange={e => setFormData({...formData, thirdPartyMaterialsNote: e.target.value})}
                           />
                           <div className="mt-3 flex items-center gap-2 text-amber-600">
                              <Info size={12} />
                              <p className="text-[8px] font-black uppercase tracking-widest">Este adendo constará no romaneio para ciência da equipe de logística.</p>
                           </div>
                        </div>
                      )}
                   </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                  <div className="space-y-4">
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Observação para o Cliente</label>
                        <textarea rows={3} className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-medium text-secondary text-[11px] resize-none focus:border-primary transition-all shadow-inner placeholder:text-slate-300" placeholder="Ex: Montagem inclusa no pavimento térreo..." value={formData.clientNote} onChange={e => setFormData({...formData, clientNote: e.target.value})} />
                     </div>
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Termos e Condições Contratuais</label>
                        <textarea rows={3} className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-medium text-secondary text-[11px] resize-none focus:border-primary transition-all shadow-inner placeholder:text-slate-300" placeholder="Ex: Pagamento via PIX 50% na aprovação..." value={formData.termsConditions} onChange={e => setFormData({...formData, termsConditions: e.target.value})} />
                     </div>
                  </div>

                  <div className="bg-secondary rounded-[32px] p-8 text-white space-y-4 shadow-2xl relative overflow-hidden flex flex-col justify-center border-4 border-white/5">
                      <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12 -translate-x-4 -translate-y-4"><DollarSign size={100} /></div>
                      
                      <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest opacity-40 relative z-10">
                        <span>Soma Parcial:</span>
                        <span>R$ {(Number(formData.subTotal) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>

                      {/* BLOCO DE DESCONTO AGREGADO CONFORME PRINT 2 */}
                      <div className="flex justify-between items-center gap-4 relative z-10">
                         <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Desconto:</span>
                         <div className="flex gap-2 items-center">
                            <div className="relative group">
                                <input 
                                    type="number"
                                    className="bg-white/5 border border-white/10 rounded-xl p-2.5 text-right font-black outline-none w-24 text-xs focus:bg-white/10 focus:border-primary transition-all text-white"
                                    value={formData.discount || ''}
                                    onChange={e => {
                                      const val = Number(e.target.value) || 0;
                                      setFormData((prev: any) => ({...prev, discount: val}));
                                    }}
                                    placeholder="0"
                                />
                            </div>
                            <select 
                                className="bg-white/10 border border-white/10 rounded-xl p-2 text-[10px] font-black outline-none text-white cursor-pointer"
                                value={formData.discountType}
                                onChange={e => setFormData({...formData, discountType: e.target.value})}
                            >
                                <option value="Percentage" className="bg-secondary text-white">%</option>
                                <option value="Fixed" className="bg-secondary text-white">R$</option>
                            </select>
                         </div>
                      </div>

                      <div className="flex justify-between items-center gap-4 relative z-10">
                         <div className="flex items-center gap-2">
                           <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Ajuste / Frete:</span>
                         </div>
                         <div className="relative group">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary font-black text-[10px]">R$</span>
                            <input 
                                type="number"
                                className="bg-white/5 border border-white/10 rounded-xl p-2.5 pl-8 text-right font-black outline-none w-32 text-xs focus:bg-white/10 focus:border-primary transition-all text-white"
                                value={formData.adjustment || ''}
                                onChange={e => {
                                  const val = Number(e.target.value) || 0;
                                  setFormData((prev: any) => ({...prev, adjustment: val}));
                                }}
                                placeholder="0,00"
                            />
                         </div>
                      </div>

                      <div className="pt-4 border-t border-white/10 flex justify-between items-end relative z-10">
                         <div>
                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-primary mb-1">Investimento Global</p>
                            <p className="text-3xl font-black tracking-tighter leading-none">R$ {(Number(finalTotal) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                         </div>
                         <div className="p-3 bg-primary rounded-2xl shadow-xl shadow-primary/20">
                            <CheckCircle2 size={24} />
                         </div>
                      </div>
                   </div>
                </div>
             </div>
          </Card>
        </div>

        <div className="lg:col-span-1 space-y-6">
          <Card className="p-6 bg-white border border-slate-100 shadow-sm rounded-[24px] space-y-8">
             
             <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                   <Tag size={14} className="text-primary" /> Marcadores (Tags)
                </label>
                <div className="flex flex-wrap gap-1.5 min-h-[30px]">
                   {formData.tags?.map((tag: any) => (
                     <Badge key={tag} className="bg-slate-50 text-slate-400 border-slate-200 text-[8px] flex items-center gap-1 px-2 py-0.5">
                        {tag} <X size={8} className="cursor-pointer hover:text-red-500" onClick={() => setFormData({...formData, tags: formData.tags?.filter((t: any) => t !== tag)})} />
                     </Badge>
                ))}
                </div>
                {showTagInput ? (
                   <form onSubmit={handleAddTag} className="flex gap-1 animate-in slide-in-from-top-1 duration-200">
                      <input autoFocus className="flex-1 bg-slate-50 border border-slate-100 p-2 rounded-lg text-[9px] outline-none uppercase font-black" value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="NOVA TAG" />
                      <button type="submit" className="bg-primary text-white p-2 rounded-lg shadow-sm"><Check size={12} /></button>
                      <button type="button" onClick={() => setShowTagInput(false)} className="bg-slate-100 text-slate-400 p-2 rounded-lg"><X size={12} /></button>
                   </form>
                ) : (
                   <button onClick={() => setShowTagInput(true)} className="text-[9px] font-black text-primary uppercase tracking-widest hover:underline">+ Criar Identificador</button>
                )}
             </div>

             <div className="space-y-5 pt-4 border-t border-slate-50">
                <div className="space-y-1.5">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <Hash size={12} className="text-primary" /> Referência Externa
                   </label>
                   <input className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl outline-none font-bold text-secondary text-[11px] focus:border-primary shadow-inner" placeholder="Ex: OS-CINE-25" value={formData.reference} onChange={e => setFormData({...formData, reference: e.target.value})} />
                </div>

                <div className="space-y-1.5">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <UserCircle size={12} className="text-primary" /> Agente Comercial
                   </label>
                   <select className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl outline-none font-bold text-secondary text-[11px] cursor-pointer shadow-inner appearance-none" value={formData.saleAgent} onChange={e => setFormData({...formData, saleAgent: e.target.value})}>
                      <option>Magnun Gestor</option>
                      <option>Admin admin</option>
                   </select>
                </div>

                <div className="space-y-1.5">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <Globe size={12} className="text-primary" /> Moeda
                   </label>
                   <select className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl outline-none font-bold text-secondary text-[11px] shadow-inner" value={formData.currency} onChange={e => setFormData({...formData, currency: e.target.value})}>
                      <option>Real (BRL R$)</option>
                      <option>Dólar (USD $)</option>
                   </select>
                </div>
             </div>

             <div className="space-y-2 pt-4 border-t border-slate-50">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <FileText size={12} className="text-primary" /> Notas Administrativas
                </label>
                <textarea rows={4} className="w-full bg-slate-50 border border-slate-100 p-3 rounded-xl outline-none font-bold text-secondary text-[10px] resize-none focus:border-primary shadow-inner placeholder:text-slate-300" placeholder="Apenas visível internamente..." value={formData.adminNote} onChange={e => setFormData({...formData, adminNote: e.target.value})} />
             </div>
          </Card>

          <div className="space-y-3">
             <button 
                onClick={() => { alert('Exportando proposta em PDF...'); onSubmit({ ...formData, total: finalTotal }); }} 
                className="w-full py-5 bg-primary text-white rounded-[24px] font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 transition-all flex items-center justify-center gap-2 active:scale-95 group"
             >
                <FileDown size={18} className="group-hover:translate-y-0.5 transition-transform" /> Confirmar e Gerar PDF
             </button>

             <button 
                onClick={() => onSubmit({ ...formData, total: finalTotal })} 
                className="w-full py-4 bg-secondary text-white rounded-[24px] font-black uppercase text-[10px] tracking-widest shadow-xl shadow-secondary/20 hover:bg-black transition-all flex items-center justify-center gap-2 active:scale-95"
             >
                <Save size={18} /> Salvar no Sistema
             </button>

             <button 
                onClick={onClose} 
                className="w-full py-4 bg-white border-2 border-slate-100 rounded-[24px] font-black uppercase text-[9px] tracking-widest text-slate-300 hover:bg-slate-50 hover:text-slate-400 transition-all active:scale-95"
             >
                Descartar Alterações
             </button>
          </div>
        </div>
      </div>

      {isLocationModalOpen && (
        <LocationModal 
          onClose={() => setIsLocationModalOpen(false)}
          onApply={(addr: any) => { setCustomLocation(addr); setIsLocationModalOpen(false); }}
          currentAddress={customLocation}
        />
      )}
    </div>
  );
};
